import {
  Box,
  Flex,
  Link,
  VStack,
  useColorModeValue,
  Icon,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Image,
  Text,
  Badge,
} from '@chakra-ui/react'
import React, { useState, useMemo } from 'react'
import { Reference } from '../../types/chatTypes'
import { ExternalLink, ChevronDown, ChevronUp } from 'lucide-react'

interface ReferencesDisplayProps {
  references?: Reference[]
}

const getDomain = (url: string): string => {
  try {
    const urlObj = new URL(url)
    let domain = urlObj.hostname.replace(/^www\./, '')
    domain = domain.replace(/\.(com|org|net|edu|gov|co\.uk|co|io|ai|app)$/, '')
    return domain
  } catch {
    return url
  }
}

const getFavicon = (url: string): string => {
  try {
    const urlObj = new URL(url)
    const fullDomain = urlObj.hostname.replace(/^www\./, '')
    return `https://icons.duckduckgo.com/ip3/${fullDomain}.ico`
  } catch {
    return `https://icons.duckduckgo.com/ip3/${url}.ico`
  }
}

const SourcePreview: React.FC<{ reference: Reference }> = ({ reference }) => {
  const [faviconError, setFaviconError] = useState(false)
  const domain = getDomain(reference.url)
  const grayLight = useColorModeValue('gray.50', 'gray.800')
  const grayBorder = useColorModeValue('gray.200', 'gray.600')
  const grayHover = useColorModeValue('gray.100', 'gray.700')
  const grayHoverBorder = useColorModeValue('gray.300', 'gray.500')
  const faviconColor = useColorModeValue('#718096', '#cbd5e0')
  const titleColor = useColorModeValue('gray.900', 'gray.50')
  const domainColor = useColorModeValue('gray.500', 'gray.400')
  const iconColor = useColorModeValue('#a0aec0', '#718096')

  return (
    <Link href={reference.url} isExternal _hover={{ textDecoration: 'none' }}>
      <Flex
        p={3}
        bg={grayLight}
        borderRadius='md'
        border={`1px solid ${grayBorder}`}
        _hover={{
          bg: grayHover,
          borderColor: grayHoverBorder,
        }}
        transition='all 0.2s'
        align='flex-start'
        gap={2.5}
        cursor='pointer'
      >
        {/* Favicon */}
        {!faviconError ? (
          <Image
            src={getFavicon(reference.url)}
            alt=''
            w={5}
            h={5}
            borderRadius='sm'
            flexShrink={0}
            mt={0.5}
            fallback={<ExternalLink size={20} color={faviconColor} />}
            onError={() => setFaviconError(true)}
          />
        ) : (
          <ExternalLink size={20} color={faviconColor} style={{ flexShrink: 0, marginTop: 8 }} />
        )}

        {/* Content */}
        <Box flex={1} minWidth={0}>
          <Text fontSize='xs' color={domainColor} fontWeight='medium' noOfLines={1}>
            {domain}
          </Text>
          <Text fontSize='sm' fontWeight={600} color={titleColor} noOfLines={2} mt={1}>
            {reference.title}
          </Text>
          {reference.page && (
            <Badge size='sm' colorScheme='blue' fontSize='10px' mt={2}>
              Page {reference.page}
            </Badge>
          )}
        </Box>

        {/* External Link Icon */}
        <ExternalLink size={16} color={iconColor} style={{ flexShrink: 0, marginTop: 16 }} />
      </Flex>
    </Link>
  )
}

const ReferencesDisplay: React.FC<ReferencesDisplayProps> = ({ references }) => {
  const [isOpen, setIsOpen] = useState(false)

  // All hooks must be called at top level
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const badgeBgColor = useColorModeValue('gray.100', 'gray.700')
  const badgeHoverColor = useColorModeValue('gray.200', 'gray.600')
  const badgeCircleBg = useColorModeValue('gray.300', 'gray.500')
  const badgeCircleText = useColorModeValue('white', 'gray.100')
  const textColor = useColorModeValue('gray.700', 'gray.200')
  const popoverBg = useColorModeValue('white', 'gray.800')

  const sortedReferences = useMemo(() => {
    if (!references) return []
    return [...references].sort((a, b) => a.title.localeCompare(b.title))
  }, [references])

  if (!references || references.length === 0) {
    return null
  }

  return (
    <Popover isOpen={isOpen} onClose={() => setIsOpen(false)} placement='top'>
      <PopoverTrigger>
        <Box
          as='button'
          onClick={() => setIsOpen(!isOpen)}
          cursor='pointer'
          _focus={{ outline: 'none' }}
          width='fit-content'
        >
          <Flex
            align='center'
            gap={1.5}
            px={2.5}
            py={1.5}
            bg={badgeBgColor}
            borderRadius='full'
            border={`1px solid ${borderColor}`}
            _hover={{ bg: badgeHoverColor }}
            transition='all 0.2s'
            fontSize='xs'
            fontWeight={500}
            color={textColor}
          >
            <Box
              display='flex'
              alignItems='center'
              justifyContent='center'
              width='16px'
              height='16px'
              borderRadius='full'
              bg={badgeCircleBg}
              color={badgeCircleText}
              fontSize='10px'
              fontWeight='bold'
            >
              {references.length}
            </Box>
            <Box>{references.length === 1 ? 'source' : 'sources'}</Box>
            <Icon as={isOpen ? ChevronUp : ChevronDown} boxSize={3} ml={0.5} transition='transform 0.2s' />
          </Flex>
        </Box>
      </PopoverTrigger>

      <PopoverContent
        bg={popoverBg}
        border={`1px solid ${borderColor}`}
        borderRadius='lg'
        boxShadow='lg'
        p={0}
        w='auto'
        maxW='420px'
      >
        <PopoverBody p={3}>
          <VStack align='stretch' spacing={2}>
            {/* Header */}
            <Box px={1} pb={2} borderBottom={`1px solid ${borderColor}`}>
              <Text fontSize='sm' fontWeight={600} color={textColor}>
                {references.length} {references.length === 1 ? 'Source' : 'Sources'}
              </Text>
            </Box>

            {/* Sources List */}
            <VStack align='stretch' spacing={2} maxH='400px' overflowY='auto'>
              {sortedReferences.map((reference, index) => (
                <SourcePreview key={index} reference={reference} />
              ))}
            </VStack>
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}

export default ReferencesDisplay
